import {
  EditorPipelineStep,
  useEditorSettings,
} from '@/services/editorService';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useEditorService,
  useFormatService,
  usePaymentService,
  usePreferencesService,
} from '@/layout/appWrapper/ServiceProvider';
import { useUser } from '@/services/userService';
import { TaskMimeType, TaskStatus, TaskType } from '@/services/uploaderService';
import { useSubscriptionUpgradePrompts } from '@/features/upgradeSubscription/useSubscriptionUpgradePrompts';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import taskToFormatAssociationState from '@/stores/formatState';
import { isReprocessingMediaState } from '@/stores/isReprocessingMediaStore';
import {
  canApplyNewPipeline,
  editorLoadingState,
  shouldApplyPipelineChanges,
  shouldResetToolbarToImageData,
} from '@/stores/editorStore';
import { mediaShownInComparerState } from '@/stores/mediaStore';
import useUploadHandler from '@/features/uploadMedia/useUploadHandler';
import { useRouter } from 'next/router';
import { useMedia } from '@/services/mediaService';
import { isLoggedInSelector } from '@/stores/authStore';
import { pushModalSelector } from '@/stores/modalStore';
import LoginToSaveSettingsModal from '@/features/imageEditor/aiToolbar/modals/LoginToSaveSettingsModal';
import { AiToolsChoices } from '@/services/settingsService/aiTypes';
import settingsState from '@/stores/settingsStore';
import { useTracker } from '@/core/tracking';
import equal from 'fast-deep-equal';
import { useLastAiToolsChoices } from '@/features/imageEditor/aiToolbar/useLastAiToolsChoices';

export const useAiToolbar = ({
  onCtaActiveChange,
  setIsAiSettingsDropdownOpen,
}: {
  onCtaActiveChange: (event: boolean) => void;
  setIsAiSettingsDropdownOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const settings = useRecoilValue(settingsState);

  const { track } = useTracker();
  const router = useRouter();
  const pushModal = useSetRecoilState(pushModalSelector);

  const setIsReprocessingMedia = useSetRecoilState(isReprocessingMediaState);
  const setIsLoadingEditor = useSetRecoilState(editorLoadingState);

  const { reprocess } = useUploadHandler('result');
  const { taskId } = router.query;

  const { data: user, mutate: mutateUser } = useUser();
  const { isBaseUser, isPersonalUser, isPremiumUser } = usePaymentService();
  const { shouldBlockPremiumFormats } = useSubscriptionUpgradePrompts();

  const taskToFormatAssociation = useRecoilValue(taskToFormatAssociationState);
  const isLoggedIn = useRecoilValue(isLoggedInSelector);

  const { extractAiPipelineFromAiToolsChoices } = useEditorService();
  const { getBulkUploadWithPolling } = useMedia();

  const mediaInComparer = useRecoilValue(mediaShownInComparerState);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const { getDefaultFormat } = useFormatService();
  const { storeNewPreferences } = usePreferencesService();
  const { buildEditorSettingsVisualization } = useEditorSettings();

  const [isCtaActive, setIsCtaActive] = useRecoilState(canApplyNewPipeline);
  const [shouldApply, setShouldApply] = useRecoilState(
    shouldApplyPipelineChanges
  );
  const [shouldResetToolbar, setShouldResetToolbar] = useRecoilState(
    shouldResetToolbarToImageData
  );
  const lastAppliedAiToolsChoices = useLastAiToolsChoices(
    settings,
    mediaInComparer.aiPipeline
  );
  const lastSavedAiToolsChoices = useLastAiToolsChoices(
    settings,
    user?.preferences?.defaultAiPipeline
  );
  const [aiToolsChoices, setAiToolsChoices] = useState<AiToolsChoices>({});

  useEffect(() => {
    onCtaActiveChange(isCtaActive);
  }, [isCtaActive, onCtaActiveChange]);

  const setAiToolsChoicesBasedOnImageModel = useCallback(() => {
    setAiToolsChoices(lastAppliedAiToolsChoices);
  }, [lastAppliedAiToolsChoices]);

  useEffect(
    () => setAiToolsChoicesBasedOnImageModel(),
    [setAiToolsChoicesBasedOnImageModel]
  );

  useEffect(() => {
    setIsCtaActive(!equal(lastAppliedAiToolsChoices, aiToolsChoices));
  }, [aiToolsChoices, lastAppliedAiToolsChoices, setIsCtaActive]);

  useEffect(() => {
    if (!shouldResetToolbar) {
      return;
    }

    // reset toolbar to the original state
    setAiToolsChoicesBasedOnImageModel();

    setShouldApply(false);
    setShouldResetToolbar(false);
  }, [
    setIsCtaActive,
    setAiToolsChoicesBasedOnImageModel,
    setShouldApply,
    setShouldResetToolbar,
    shouldResetToolbar,
  ]);

  const onToggleChange = useCallback(
    (toggleEvent: { tool: string; checked: boolean; model: object }) => {
      if (!toggleEvent.checked) {
        const currentAiToolsChoices = { ...aiToolsChoices };

        delete currentAiToolsChoices[toggleEvent.tool];

        setAiToolsChoices(currentAiToolsChoices);
      } else {
        setAiToolsChoices((prev) => ({
          ...prev,
          [toggleEvent.tool]: toggleEvent.model,
        }));
      }
    },
    [aiToolsChoices]
  );

  const onVariantsChange = useCallback(
    ({ tool, model }: { tool: string; model: object }) => {
      setAiToolsChoices((prev) => ({
        ...prev,
        [tool]: model,
      }));
    },
    []
  );

  const onSelectPreview = useCallback(
    ({
      model,
      pipelineStep,
    }: {
      model: object;
      pipelineStep: EditorPipelineStep;
    }) => {
      setAiToolsChoices((prev) => ({
        ...prev,
        [pipelineStep.tool]: model,
      }));
    },
    []
  );

  const onRemovePipelineStep = useCallback(
    ({ pipelineStep }: { pipelineStep: EditorPipelineStep }) => {
      const currentAiToolsChoices = { ...aiToolsChoices };

      delete currentAiToolsChoices[pipelineStep.tool];

      setAiToolsChoices(currentAiToolsChoices);
    },
    [aiToolsChoices]
  );

  const outputFormat = useMemo(
    () => taskToFormatAssociation[mediaInComparer.id!] || getDefaultFormat(),
    [getDefaultFormat, mediaInComparer.id, taskToFormatAssociation]
  );

  const canGetRequestedOutputFormat = useMemo(() => {
    if (!user) {
      return false;
    }

    return (
      isPremiumUser(user) &&
      !(outputFormat.isBusinessOnly && shouldBlockPremiumFormats)
    );
  }, [
    isPremiumUser,
    outputFormat.isBusinessOnly,
    shouldBlockPremiumFormats,
    user,
  ]);

  const currentAiPipelineChoices = useMemo(
    () =>
      extractAiPipelineFromAiToolsChoices(
        aiToolsChoices,
        settings.values,
        outputFormat.additionalPipelineParams
      ),
    [
      aiToolsChoices,
      extractAiPipelineFromAiToolsChoices,
      outputFormat.additionalPipelineParams,
      settings.values,
    ]
  );

  const onSubmitEditorConfig = useCallback(async () => {
    setIsLoadingEditor(true);
    setIsReprocessingMedia(true);

    const reprocessedMedia = await reprocess({
      currentBulkUploadId: taskId as string,
      bulkUploadReprocessPayload: {
        inputReprocessList: [
          {
            aiPipeline: currentAiPipelineChoices,
            clientTaskId: mediaInComparer.id!,
            outputContentType: canGetRequestedOutputFormat
              ? outputFormat.mimeType
              : TaskMimeType.JPG,
          },
        ],
      },
    });

    const taskListCompleted = reprocessedMedia.taskList?.every(
      (t) => t.status === TaskStatus.Completed
    );

    if (!taskListCompleted) {
      await getBulkUploadWithPolling({ isBulkProcessingCompleted: false });
    } else {
      setIsReprocessingMedia(false);
      setIsLoadingEditor(false);
      track(TaskStatus.Reprocessing, TaskStatus.Completed, {
        actionValue: 'browse',
        mediaType: TaskType.Image,
        bulkUploadId: reprocessedMedia.bulkUploadId,
        mediaQuantity: reprocessedMedia.taskList?.length,
        aiPipeline: aiToolsChoices,
      });
    }

    track('editor_image_model', 'selected', {
      aiToolbarSelection: String(Object.keys(aiToolsChoices)),
    });
  }, [
    aiToolsChoices,
    canGetRequestedOutputFormat,
    currentAiPipelineChoices,
    getBulkUploadWithPolling,
    mediaInComparer.id,
    outputFormat.mimeType,
    reprocess,
    setIsLoadingEditor,
    setIsReprocessingMedia,
    taskId,
    track,
  ]);

  const isCurrentPipelineDefaultForUser = useMemo(() => {
    if (!user?.preferences?.defaultAiPipeline) {
      return false;
    }

    return equal(lastSavedAiToolsChoices, aiToolsChoices);
  }, [
    aiToolsChoices,
    lastSavedAiToolsChoices,
    user?.preferences?.defaultAiPipeline,
  ]);

  const storeNewDefaultAiPipeline = useCallback(async () => {
    if (!user) {
      return;
    }

    if (isCurrentPipelineDefaultForUser) {
      return;
    }

    if (!isLoggedIn) {
      setIsAiSettingsDropdownOpen(false);
      track('ai_settings', 'saving_modal_shown');

      pushModal({
        id: 'login-to-save',
        Modal: LoginToSaveSettingsModal,
      });
      return;
    }

    setIsSavingSettings(true);

    const currentAiPipelineWithoutFormat = extractAiPipelineFromAiToolsChoices(
      aiToolsChoices,
      settings.values
    );
    await storeNewPreferences({
      ...user.preferences!,
      defaultAiPipeline: currentAiPipelineWithoutFormat,
    });

    track('ai_settings', 'saved', {
      aiPipeline: currentAiPipelineWithoutFormat,
    });

    await mutateUser();
    setIsSavingSettings(false);
  }, [
    aiToolsChoices,
    extractAiPipelineFromAiToolsChoices,
    isCurrentPipelineDefaultForUser,
    isLoggedIn,
    mutateUser,
    pushModal,
    setIsAiSettingsDropdownOpen,
    settings.values,
    storeNewPreferences,
    track,
    user,
  ]);

  useEffect(() => {
    if (shouldApply && isCtaActive) {
      onSubmitEditorConfig();
      setShouldApply(false);
    }
  }, [
    shouldApply,
    setShouldApply,
    isCtaActive,
    onSubmitEditorConfig,
    setIsCtaActive,
  ]);

  const onApplyClick = useCallback(async () => {
    await onSubmitEditorConfig();
  }, [onSubmitEditorConfig, setIsCtaActive]);

  const shouldBlockPipelineStepForUser = useCallback(
    ({ pipelineStep }: { pipelineStep: EditorPipelineStep }): boolean => {
      if (!user) {
        return false;
      }

      return (
        (isBaseUser(user) && !!pipelineStep.isBlockedForNonSubscribers) ||
        (isPersonalUser(user) && !!pipelineStep.isBlockedForPersonalUsers)
      );
    },
    [isBaseUser, isPersonalUser, user]
  );

  return {
    aiToolsChoices,
    aiToolbarElements: buildEditorSettingsVisualization(),
    isCtaActive,
    isCurrentPipelineDefaultForUser,
    isSavingSettings,
    lastAppliedAiToolsChoices,
    onApplyClick,
    onRemovePipelineStep,
    onSelectPreview,
    onSubmitEditorConfig,
    onToggleChange,
    onVariantsChange,
    shouldBlockPipelineStepForUser,
    storeNewDefaultAiPipeline,
  };
};
